import type { StorePageEditResult } from "../store-page/types";
import { storePageApi } from "../store-page/client";
import type { useStorePageDraft } from "./use-store-page-draft";

type Draft = ReturnType<typeof useStorePageDraft>;
type Write = Readonly<{
  slug: string;
  draft: Pick<
    Draft,
    | "content"
    | "baseRevisionId"
    | "contentVersion"
    | "cancelRead"
    | "applySavedDraft"
    | "setLastAction"
  >;
}>;

export function saveDraftOperation({
  slug,
  draft,
  note,
}: Write & { readonly note: string | null }) {
  const key = crypto.randomUUID();
  const submittedContentVersion = draft.contentVersion.current;
  return {
    operation: "save" as const,
    run: async () => {
      await draft.cancelRead();
      const result = await storePageApi.saveDraft({
        slug,
        content: draft.content,
        baseRevisionId: draft.baseRevisionId,
        note,
        idempotencyKey: key,
      });
      await draft.applySavedDraft({ result, submittedContentVersion });
      return result;
    },
  };
}

export function publishDraftOperation({
  slug,
  draft,
  refresh,
}: Write & { readonly refresh: () => Promise<void> }) {
  const saveKey = crypto.randomUUID();
  const publishKey = crypto.randomUUID();
  const submittedContentVersion = draft.contentVersion.current;
  let saved: StorePageEditResult | null = null;
  return {
    operation: "publish" as const,
    run: async () => {
      await draft.cancelRead();

      if (!saved) {
        saved = await storePageApi.saveDraft({
          slug,
          content: draft.content,
          baseRevisionId: draft.baseRevisionId,
          note: null,
          idempotencyKey: saveKey,
        });
        await draft.applySavedDraft({ result: saved, submittedContentVersion });
      }

      const published = await storePageApi.publish({
        slug,
        baseRevisionId: saved.revision.id,
        idempotencyKey: publishKey,
      });
      await draft.applySavedDraft({
        result: published,
        submittedContentVersion,
      });
      draft.setLastAction(
        published.revision.status === "pending_review"
          ? "pending_review"
          : "published",
      );
    },
    refresh,
  };
}

export function changeRevisionOperation({
  operation,
  draft,
  change,
  refresh,
}: {
  readonly operation: "approve" | "reject" | "revert";
  readonly draft: Pick<
    Draft,
    "contentVersion" | "cancelRead" | "applyRevision"
  >;
  readonly change: (key: string) => Promise<StorePageEditResult>;
  readonly refresh: () => Promise<void>;
}) {
  const key = crypto.randomUUID();
  const submittedContentVersion = draft.contentVersion.current;
  return {
    operation,
    run: async () => {
      await draft.cancelRead();
      const result = await change(key);
      await draft.applyRevision({ operation, result, submittedContentVersion });
      return result;
    },
    refresh,
  };
}
